import { useState } from 'react';
import { api, errorMessage } from '../lib/api';
import type { ReviewPart } from '../lib/tests';

/**
 * The paragraphs of a reading passage, and how to correct them.
 *
 * A pasted passage does not say where its paragraphs are. Where the source
 * left a blank line between them, or pushed the first line in, the importer
 * knows; where it did neither — copying out of a PDF usually gives neither —
 * it reads the wrapping instead, and a paragraph whose last line happens to
 * fill its margin is invisible to that. About one break in ten.
 *
 * So the guess is shown with a count beside it and a box to correct it in,
 * holding the passage in the shape a teacher would have pasted: a blank line
 * between paragraphs, a letter in front of a lettered one. Nothing has to be
 * re-imported, and papers that came in before any of this can be fixed where
 * they stand.
 */
export function PassageEditor({
  testId,
  part,
  onSaved,
}: {
  testId: string;
  part: ReviewPart;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(part.passageText ?? '');
  const [title, setTitle] = useState(part.passageTitle ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** What the paper holds now, and what the box would make of it. */
  const shown = (part.passageHtml?.match(/<p[ >]/g) ?? []).length;
  const drafted = text.split(/\n\s*\n/).filter((block) => block.trim()).length;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/tests/${testId}/parts/${part.index}`, {
        passageText: text,
        passageTitle: title.trim() || null,
      });
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(errorMessage(err, 'Could not save the passage'));
    } finally {
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <div className="passage__foot">
        <p className="muted">
          {shown} paragraph{shown === 1 ? '' : 's'}
        </p>
        <button
          className="btn btn--ghost"
          onClick={() => {
            setText(part.passageText ?? '');
            setTitle(part.passageTitle ?? '');
            setEditing(true);
          }}
        >
          Edit the passage
        </button>
      </div>
    );
  }

  return (
    <div className="passage__edit stack">
      <div>
        <span className="field__label">The passage, as text</span>
        <p className="field__hint">
          One blank line between paragraphs — that is the whole of it. A lettered paragraph keeps
          its letter at the front, as in <span className="mono">A&nbsp;&nbsp;Marie Curie was…</span>{' '}
          A short opening line with no full stop is taken as the title, and the sentence under it as
          the standfirst, set in italic.
        </p>
      </div>

      {/*
        * The title, kept apart from the body.
        *
        * It is set in bold above the passage and it is not a paragraph of it,
        * so a teacher who wants to correct one should not have to work out
        * which line of the box the screen will treat as a heading.
        */}
      <div className="field">
        <label className="field__label" htmlFor={`passage-title-${part.index}`}>
          Title
        </label>
        <input
          id={`passage-title-${part.index}`}
          className="field__input"
          value={title}
          disabled={saving}
          placeholder="Living with artificial intelligence"
          onChange={(e) => setTitle(e.target.value)}
        />
      </div>

      <textarea
        className="writer__editor"
        spellCheck={false}
        value={text}
        onChange={(e) => setText(e.target.value)}
        aria-label={`Passage ${part.index}`}
      />

      {error && <p className="field__error">{error}</p>}

      <div className="passage__foot">
        <p className="muted">
          {drafted} paragraph{drafted === 1 ? '' : 's'}
          {drafted !== shown && shown > 0 ? ` — the paper has ${shown} now` : ''}
        </p>
        <div className="row" style={{ gap: 'var(--s-2)' }}>
          <button className="btn btn--ghost" disabled={saving} onClick={() => setEditing(false)}>
            Cancel
          </button>
          <button className="btn" disabled={saving} onClick={() => void save()}>
            {saving ? 'Saving…' : 'Save passage'}
          </button>
        </div>
      </div>
    </div>
  );
}
